import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Facebook, Instagram, Linkedin, Youtube, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import BehencharaLogo from './BehencharaLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOCIAL_LINKS = {
  facebook: 'https://www.facebook.com/profile.php?id=61591658203960',
  instagram: 'https://www.instagram.com/bc.behenchara?stkn=ZjZhMW8yeHAwNmFr',
  linkedin: 'https://www.linkedin.com/company/behenchara/',
  youtube: 'https://www.youtube.com/channel/UCfQd2DCY5ov7w3gZn7-QLDQ'
};

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/magazine/subscribe`, { email });
      toast.success(response.data.message);
      setEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-charcoal text-ivory" data-testid="main-footer">
      {/* Newsletter Section */}
      <div className="border-b border-charcoal-muted">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <p className="label-editorial text-terracotta mb-4">The Behenchara Letter</p>
          <h2 className="font-serif text-4xl md:text-5xl leading-tight text-ivory mb-6">
            A letter from your sisterhood.
          </h2>
          <p className="text-base text-ivory/70 max-w-xl mx-auto mb-8">
            Stories, ideas, opportunities and conversations worth knowing about — delivered to your inbox.
          </p>
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto" data-testid="footer-newsletter-form">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Your email address"
              className="flex-1 px-5 py-3 bg-transparent border border-ivory/30 text-ivory placeholder:text-ivory/40 focus:outline-none focus:border-terracotta"
              data-testid="footer-newsletter-email-input"
            />
            <button
              type="submit"
              disabled={loading}
              className="px-8 py-3 bg-ivory text-charcoal hover:bg-beige transition-colors font-medium text-sm uppercase tracking-widest disabled:opacity-50"
              data-testid="footer-newsletter-submit-button"
            >
              {loading ? 'Sending' : 'Join'}
            </button>
          </form>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-10">
          <div className="md:col-span-2 space-y-6">
            <div className="bg-ivory p-4 inline-block rounded-sm">
              <BehencharaLogo className="h-12 w-auto" showText={true} />
            </div>
            <p className="text-sm text-ivory/70 leading-relaxed max-w-sm">
              A global space for women to share, learn, question, connect and grow — together.
            </p>
            <div className="flex space-x-4">
              <a href={SOCIAL_LINKS.facebook} target="_blank" rel="noopener noreferrer" className="text-ivory/60 hover:text-terracotta transition-colors" data-testid="footer-facebook-link">
                <Facebook className="h-4 w-4" />
              </a>
              <a href={SOCIAL_LINKS.instagram} target="_blank" rel="noopener noreferrer" className="text-ivory/60 hover:text-terracotta transition-colors" data-testid="footer-instagram-link">
                <Instagram className="h-4 w-4" />
              </a>
              <a href={SOCIAL_LINKS.linkedin} target="_blank" rel="noopener noreferrer" className="text-ivory/60 hover:text-terracotta transition-colors" data-testid="footer-linkedin-link">
                <Linkedin className="h-4 w-4" />
              </a>
              <a href={SOCIAL_LINKS.youtube} target="_blank" rel="noopener noreferrer" className="text-ivory/60 hover:text-terracotta transition-colors" data-testid="footer-youtube-link">
                <Youtube className="h-4 w-4" />
              </a>
            </div>
          </div>

          <div>
            <p className="label-editorial text-terracotta mb-4">Explore</p>
            <ul className="space-y-3">
              <li><Link to="/articles" className="text-sm text-ivory/70 hover:text-ivory transition-colors">Magazine</Link></li>
              <li><Link to="/articles" className="text-sm text-ivory/70 hover:text-ivory transition-colors">Stories</Link></li>
              <li><Link to="/community" className="text-sm text-ivory/70 hover:text-ivory transition-colors">Community</Link></li>
              <li><Link to="/articles" className="text-sm text-ivory/70 hover:text-ivory transition-colors">Research</Link></li>
            </ul>
          </div>

          <div>
            <p className="label-editorial text-terracotta mb-4">Sisterhood</p>
            <ul className="space-y-3">
              <li><Link to="/careers" className="text-sm text-ivory/70 hover:text-ivory transition-colors">Opportunities</Link></li>
              <li><Link to="/submit-story" className="text-sm text-ivory/70 hover:text-ivory transition-colors">Share Your Story</Link></li>
              <li><Link to="/careers" className="text-sm text-ivory/70 hover:text-ivory transition-colors">Join Us</Link></li>
              <li><Link to="/events" className="text-sm text-ivory/70 hover:text-ivory transition-colors">Events</Link></li>
            </ul>
          </div>

          <div>
            <p className="label-editorial text-terracotta mb-4">About</p>
            <ul className="space-y-3">
              <li><Link to="/community" className="text-sm text-ivory/70 hover:text-ivory transition-colors">Our Vision</Link></li>
              <li><Link to="/contact" className="text-sm text-ivory/70 hover:text-ivory transition-colors">Contact</Link></li>
              <li><Link to="/privacy" className="text-sm text-ivory/70 hover:text-ivory transition-colors">Privacy</Link></li>
              <li><Link to="/terms" className="text-sm text-ivory/70 hover:text-ivory transition-colors">Terms</Link></li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-charcoal-muted flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <p className="text-xs text-ivory/50">
            © {currentYear} Behenchara. All rights reserved.
          </p>
          <p className="label-editorial text-terracotta">Sisters Empowering Sisters</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
